import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  ReactNode
} from "react";
import { Requester } from "../../utils/Requester";
import { useChart } from "../../contexts/ChartDataContext";
import { DateRange, ReportConfig } from "../../types/types";
import { ReportConfigCards } from "./ReportConfigCards";
import { SimpleSelect } from "../Misc/SimpleSelect";
import style from "./ReportConfigView.module.scss"
import moment from "moment";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { ReportConfigModal } from "./ReportConfigModal";
import FileDownload from "js-file-download";
import "moment-timezone"
import { BannerStyle, InfoBanner } from "../Misc/InfoBanner";
import { DateRangePicker } from "../DatePicker/DateRangePicker";
import { SpinnyThingy } from "../Misc/SpinnyThingy";

export function ReportConfigView() {
  const chart = useChart();
  const { installation, availableDataRange } = chart;

  const { t } = useTranslation();
  const presetRanges = useMemo(() => [
    { label: t("Last 2 weeks"), value: "last2weeks" },
    { label: t("Last month"), value: "lastMonth" },
    { label: t("All available"), value: "allAvailable" },
    { label: t("Custom"), value: "custom" },
  ], [t]);

  const [currentModal, setCurrentModal] = useState<ReactNode>(undefined);
  const [reportConfigs, setReportConfigs] = useState<ReportConfig[]>();
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    start: moment().subtract(14, "days").toDate(),
    end: moment().toDate(),
  }));
  const [selectedPreset, setSelectedPreset] = useState(presetRanges[0]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Check if selected date range overlaps with available data range
  const hasData =
    dateRange.end >= availableDataRange.start &&
    dateRange.start <= availableDataRange.end;

  const refreshConfigs = useCallback(() => {
    setIsLoading(true);
    Requester.getReportConfigs(installation.pk)
      .then((response) => {
        setReportConfigs(response.report_configs);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [installation.pk]);

  const generateReport = (params: Array<string>) => {
    if (!hasData) {
      toast.error(t("The selected time frame does not have any data!"));
      return;
    }

    const date: Date = new Date();

    const paramsWithDate = params.map(param => {
      const urlSearchParams = new URLSearchParams(param);
      urlSearchParams.set("x1", dateRange.start.toISOString());
      urlSearchParams.set("x2", dateRange.end.toISOString());
      // NOTE: Single-chart report uses generateQueryParamString() helper
      //       but we don't have `params` as `ChartGetParams` type to pass
      //       it to here.
      // FIXME: This means we need to pass timezone in **both** the
      //        `reportRequest` and `paramsWithDate` to make timezone work
      //        correctly.
      urlSearchParams.set("timezone", moment.tz.guess());
      return `?${urlSearchParams.toString()}`;
    });

    const reportRequest = {
      data: [{
        installation: chart.installation.pk.toString(),
        chartParams: paramsWithDate,
        materials: Object.fromEntries(chart.materialMap),
      }],
      timezone: moment.tz.guess(),
      lang: localStorage.getItem("lang") || "en",
    };
    toast.success(t("Generating report..."));
    const p = Requester.downloadGenerateReport(reportRequest)
      .then((response) => {
        FileDownload(
          response.data,
          `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}_report.zip`
        );
        toast.success(t("Download successful!"));
      })
      .catch(() => {
        toast.error(t("Unexpected error when generating report!"));
      })
    return p
  };

  useEffect(() => {
    refreshConfigs();
  }, [refreshConfigs]);

  useEffect(() => {
    switch (selectedPreset.value) {
      case "last2weeks":
        setDateRange({
          start: moment().subtract(14, "days").toDate(),
          end: moment().toDate(),
        });
        break;
      case "lastMonth":
        setDateRange({
          start: moment().subtract(1, "month").toDate(),
          end: moment().toDate(),
        });
        break;
      case "allAvailable":
        if (availableDataRange?.start && availableDataRange?.end) {
          setDateRange({
            start: moment(availableDataRange.start).toDate(),
            end: moment(availableDataRange.end).toDate(),
          });
        }
        break;
    }
  }, [selectedPreset, availableDataRange]);

  useEffect(() => {
    if (chart.chartParams.x1 && chart.chartParams.x2) {
      const chartDateRange: DateRange = {
        start: chart.chartParams.x1,
        end: chart.chartParams.x2
      };

      setDateRange(chartDateRange);

      if (chart.chartParams.allData) {
        setSelectedPreset(presetRanges[2]);
      } else {
        const twoWeeksAgo = moment().subtract(14, "days");
        if (moment(chartDateRange.start).isSame(twoWeeksAgo, "day") &&
          moment(chartDateRange.end).isSame(moment(), "day")) {
          setSelectedPreset(presetRanges[0]);
        } else {
          setSelectedPreset(presetRanges[3]);
        }
      }
    }
  }, [chart.chartParams.x1, chart.chartParams.x2, chart.chartParams.allData, presetRanges])

  return <div className={style.pageWrapper}>
    <div>
      <div className={style.datePicker}>
        <div>
          <p>{t("Select timeframe")}</p>
          <SimpleSelect
            selectedOption={selectedPreset}
            options={presetRanges}
            getOptionLabel={(opt) => opt.label}
            isClearable={false}
            onChange={(opt) => setSelectedPreset(opt)}
          />
        </div>
        <DateRangePicker
          onRangeSelect={(start, end) => {
            if (start && end) {
              setDateRange({ start: start, end: end });
              setSelectedPreset(presetRanges[3]);
            }
          }}
          start={dateRange.start}
          end={dateRange.end}
          min={availableDataRange.start}
          max={availableDataRange.end}
        />
      </div>
      {!hasData &&
        <div style={{marginTop:"0.5rem"}}>
          <InfoBanner style={BannerStyle.WARNING} >
            {t("The selected time frame does not have any data!")}
          </InfoBanner>
        </div>
      }
    </div>
    {isLoading ?
      <SpinnyThingy />
      :
      <div className={style.cardWrapper}>
        <ReportConfigCards
          reportConfigs={reportConfigs}
          refreshConfigs={refreshConfigs}
          openModal={(config) => {
            setCurrentModal(
              <ReportConfigModal
                // This forces the modal to be a new instance in order to refresh
                // the internal states
                key={Date.now()}
                dateRange={dateRange}
                installationId={installation.pk}
                refreshConfigs={refreshConfigs}
                reportConfig={config}
                nodes={chart.nodes}
                configNames={reportConfigs?.map((cfg) => cfg.name) || []}
              />
            );
          }}
          handleDownload={generateReport}
        />
      </div>
    }
    {currentModal}
  </div>
}
